import React from 'react';
import { Brain, Layers, TrendingUp, Shield, BarChart3, Activity, Zap, Clock } from 'lucide-react';
import SignupNotification from '../components/SignupNotification';

export default function MachineLearningAI() {
  const logoSrc = '/logo.png';

  return (
    <div className="min-h-screen bg-[#111315] text-white">
      <SignupNotification />

      <nav className="fixed top-0 w-full bg-[#111315]/95 backdrop-blur-xl border-b border-neutral-800 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <img src={logoSrc} alt="HDAlgos logo" className="h-10 w-auto" />
              <span className="sr-only">HDAlgos Institutional Systems</span>
            </div>
            <div className="hidden md:flex items-center space-x-6">
              <a href="/" className="text-sm text-neutral-400 hover:text-white transition">Home</a>
              <a href="/#systems" className="text-sm text-neutral-400 hover:text-white transition">Systems</a>
              <a href="/#tiers" className="text-sm text-neutral-400 hover:text-white transition">Pricing</a>
              <a href="/#performance" className="text-sm text-neutral-400 hover:text-white transition">Performance</a>
              <a href="/#about" className="text-sm text-neutral-400 hover:text-white transition">About</a>
              <a href="/#faq" className="text-sm text-neutral-400 hover:text-white transition">FAQ</a>
              <a href="/#contact" className="text-sm text-neutral-400 hover:text-white transition">Contact</a>
              <span className="text-sm font-bold text-[#CBB26A]">AI / Machine Learning</span>
            </div>
          </div>
        </div>
      </nav>

      <main className="pt-32 pb-24 px-6 bg-gradient-to-b from-[#1c1d20] via-[#111315] to-[#0d0e10]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black leading-tight mb-8">
              AI & Machine Learning
              <br />
              <span className="text-[#CBB26A]">Adaptive Intelligence for the Elite Systems</span>
            </h1>
            <p className="text-2xl text-neutral-400 max-w-4xl mx-auto leading-relaxed">
              Each Elite-tier module is supported by a continuously trained machine-learning layer that keeps the models
              statistically aligned with live market behaviour.
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              <div className="space-y-12">
                <div className="bg-gradient-to-br from-[#1c1d20] to-[#111315] border-2 border-[#CBB26A]/30 p-12 rounded-2xl">
                  <p className="text-xl text-neutral-300 leading-relaxed mb-6">
                    Within the Elite systems, machine learning isn&apos;t a marketing term — it&apos;s a core implementation.
                  </p>
                  <p className="text-lg text-neutral-400 leading-relaxed">
                    Each algorithm is trained, validated, and periodically re-fitted for specific assets and timeframes, ensuring
                    that the model&apos;s logic evolves with the unique volatility and structure of each market.
                  </p>
                </div>

                <div>
                  <h2 className="text-3xl font-black mb-8 text-[#CBB26A]">How It Works</h2>

                  <div className="space-y-8">
                    <div className="bg-neutral-950 border-2 border-neutral-800 p-8 rounded-xl">
                      <div className="text-xl font-bold text-white mb-3">Asset-Specific Training</div>
                      <p className="text-neutral-400 leading-relaxed mb-3">
                        Every asset — whether it&apos;s equity index, commodity, or FX — has its own data signature.
                      </p>
                      <p className="text-neutral-400 leading-relaxed">
                        Models are trained individually on historical return and volatility features unique to that instrument,
                        rather than using a one-size-fits-all dataset.
                      </p>
                    </div>

                    <div className="bg-neutral-950 border-2 border-neutral-800 p-8 rounded-xl">
                      <div className="text-xl font-bold text-white mb-3">Time-Frame Calibration</div>
                      <p className="text-neutral-400 leading-relaxed mb-3">
                        Regime and covariance models are optimised separately for each timeframe (daily, 4-hour, 1-hour etc.),
                        capturing distinct rhythm and mean-reversion speeds.
                      </p>
                      <p className="text-neutral-400 leading-relaxed">
                        This multi-horizon training mirrors how institutional trading desks run multiple “models per bucket” to reflect
                        differing behavioural dynamics.
                      </p>
                    </div>

                    <div className="bg-neutral-950 border-2 border-neutral-800 p-8 rounded-xl">
                      <div className="text-xl font-bold text-white mb-3">Rolling Refit Schedule</div>
                      <p className="text-neutral-400 leading-relaxed mb-3">
                        The models are retrained offline on rolling datasets — typically every few weeks or months depending on volatility
                        conditions — to ensure parameters like transition probabilities, volatility clusters, and covariance structures stay current.
                      </p>
                      <p className="text-neutral-400 leading-relaxed">
                        When statistical drift is detected, the refit automatically updates internal coefficients before the next live session.
                      </p>
                    </div>

                    <div className="bg-neutral-950 border-2 border-neutral-800 p-8 rounded-xl">
                      <div className="text-xl font-bold text-white mb-3">Regime & Covariance Integration</div>
                      <p className="text-neutral-400 leading-relaxed mb-4">
                        The trained models feed directly into systems such as:
                      </p>
                      <ul className="space-y-3 ml-4">
                        <li className="flex items-start gap-3">
                          <div className="w-2 h-2 bg-[#CBB26A] rounded-full mt-2 flex-shrink-0"></div>
                          <div>
                            <span className="text-white font-bold">Regime Detection:</span>{' '}
                            <span className="text-neutral-400">
                              adaptive HMM classifier that updates state probabilities for trend vs. chop environments.
                            </span>
                          </div>
                        </li>
                        <li className="flex items-start gap-3">
                          <div className="w-2 h-2 bg-[#CBB26A] rounded-full mt-2 flex-shrink-0"></div>
                          <div>
                            <span className="text-white font-bold">Machine-Learning Regime/Covariance Hybrid:</span>{' '}
                            <span className="text-neutral-400">
                              ensemble that merges regime probabilities with real-time covariance scaling, dynamically adjusting exposure
                              based on both environment and correlation breakdowns.
                            </span>
                          </div>
                        </li>
                      </ul>
                    </div>

                    <div className="bg-neutral-950 border-2 border-neutral-800 p-8 rounded-xl">
                      <div className="text-xl font-bold text-white mb-3">Continuous Validation</div>
                      <p className="text-neutral-400 leading-relaxed">
                        Each re-trained model is benchmarked against out-of-sample periods to confirm stability and prevent overfitting —
                        exactly as a risk-managed quant desk would validate its signal engines.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-8">
                <div className="bg-neutral-900 border-2 border-[#CBB26A]/40 p-12 rounded-xl h-full">
                  <h2 className="text-3xl font-black mb-8 text-[#CBB26A] text-center">Machine Learning Pipeline</h2>

                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-[#CBB26A]/20 to-[#A8914D]/20 border-2 border-[#CBB26A]/50 rounded-lg flex items-center justify-center">
                        <Brain className="w-10 h-10 text-[#CBB26A]" />
                      </div>
                      <div>
                        <div className="text-lg font-bold text-white mb-1">Data Ingestion</div>
                        <div className="text-sm text-neutral-500">Historical prices, volatility, returns</div>
                      </div>
                    </div>

                    <div className="h-px bg-neutral-800"></div>

                    <div className="flex items-start gap-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-[#CBB26A]/20 to-[#A8914D]/20 border-2 border-[#CBB26A]/50 rounded-lg flex items-center justify-center">
                        <Layers className="w-10 h-10 text-[#CBB26A]" />
                      </div>
                      <div>
                        <div className="text-lg font-bold text-white mb-1">Feature Engineering</div>
                        <div className="text-sm text-neutral-500">Extracted signal features and transformations</div>
                      </div>
                    </div>

                    <div className="h-px bg-neutral-800"></div>

                    <div className="flex items-start gap-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-[#CBB26A]/20 to-[#A8914D]/20 border-2 border-[#CBB26A]/50 rounded-lg flex items-center justify-center">
                        <TrendingUp className="w-10 h-10 text-[#CBB26A]" />
                      </div>
                      <div>
                        <div className="text-lg font-bold text-white mb-1">Train / Fit</div>
                        <div className="text-sm text-neutral-500">Optimise parameters across models</div>
                      </div>
                    </div>

                    <div className="h-px bg-neutral-800"></div>

                    <div className="flex items-start gap-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-[#CBB26A]/20 to-[#A8914D]/20 border-2 border-[#CBB26A]/50 rounded-lg flex items-center justify-center">
                        <Shield className="w-10 h-10 text-[#CBB26A]" />
                      </div>
                      <div>
                        <div className="text-lg font-bold text-white mb-1">Validate</div>
                        <div className="text-sm text-neutral-500">Cross-validation and stress checks</div>
                      </div>
                    </div>

                    <div className="h-px bg-neutral-800"></div>

                    <div className="flex items-start gap-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-[#CBB26A]/20 to-[#A8914D]/20 border-2 border-[#CBB26A]/50 rounded-lg flex items-center justify-center">
                        <BarChart3 className="w-10 h-10 text-[#CBB26A]" />
                      </div>
                      <div>
                        <div className="text-lg font-bold text-white mb-1">Test</div>
                        <div className="text-sm text-neutral-500">Out-of-sample verification</div>
                      </div>
                    </div>

                    <div className="h-px bg-neutral-800"></div>

                    <div className="flex items-start gap-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-[#CBB26A]/20 to-[#A8914D]/20 border-2 border-[#CBB26A]/50 rounded-lg flex items-center justify-center">
                        <Activity className="w-10 h-10 text-[#CBB26A]" />
                      </div>
                      <div>
                        <div className="text-lg font-bold text-white mb-1">Reweight</div>
                        <div className="text-sm text-neutral-500">Adjust conviction and exposure weights</div>
                      </div>
                    </div>

                    <div className="h-px bg-neutral-800"></div>

                    <div className="flex items-start gap-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-[#CBB26A]/20 to-[#A8914D]/20 border-2 border-[#CBB26A]/50 rounded-lg flex items-center justify-center">
                        <Zap className="w-10 h-10 text-[#CBB26A]" />
                      </div>
                      <div>
                        <div className="text-lg font-bold text-white mb-1">Deploy</div>
                        <div className="text-sm text-neutral-500">Release into live production</div>
                      </div>
                    </div>

                    <div className="h-px bg-neutral-800"></div>

                    <div className="flex items-start gap-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-[#CBB26A]/20 to-[#A8914D]/20 border-2 border-[#CBB26A]/50 rounded-lg flex items-center justify-center">
                        <Clock className="w-10 h-10 text-[#CBB26A]" />
                      </div>
                      <div>
                        <div className="text-lg font-bold text-white mb-1">Refit</div>
                        <div className="text-sm text-neutral-500">Schedule rolling updates</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center bg-neutral-900 border-2 border-[#CBB26A]/30 p-8 rounded-xl mt-12">
              <p className="text-2xl font-bold text-[#CBB26A]">
                Every asset, every timeframe — continuously learned, refit, and validated.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
